import { create } from "zustand";
import axios from "axios";
import { supabase } from "../helper/supabaseClient";
import secureLocalStorage from "react-secure-storage";
import { auth } from "../helper/firebaseClient";
import { api } from "../utils/axios-instance";

const normalizeUser = (user) => {
  if (!user) return null;

  const flags = user.flags || {};
  const location = user?.metadata?.location || {};

  // Canonical dept: prefer arrays; keep legacy dept ONLY as fallback read
  const deptIds = Array.isArray(user.dept_ids) ? user.dept_ids : [];
  const deptNames = Array.isArray(user.dept_names) ? user.dept_names : [];

  const derived = {
    is_authenticated: user.is_authenticated ?? flags.is_authenticated ?? false,
    is_approved: user.is_approved ?? flags.is_approved ?? false,
    is_active: user.is_active ?? flags.is_active ?? false,
    email_verified: user.email_verified ?? flags.email_verified ?? false,
  };

  return {
    ...user,

    // Canonicalize location for UI reads (no new root fields created)
    metadata: {
      ...(user.metadata || {}),
      location: {
        country: location.country ?? user.country ?? "Unknown",
        city: location.city ?? user.city ?? "Unknown",
      },
    },

    // Canonicalize dept arrays for UI reads
    dept_ids: deptIds.length ? deptIds : [],
    dept_names: deptNames.length ? deptNames : [],

    // DO NOT inject legacy root duplicates into the object:
    // (No user.is_active, user.email_verified, user.country, user.city, user.dept etc.)

    // Keep flags map always present & canonical
    flags: {
      is_authenticated: derived.is_authenticated,
      is_approved: derived.is_approved,
      is_active: derived.is_active,
      email_verified: derived.email_verified,
    },

    // Provide flattened flags for guards WITHOUT polluting root keys
    derivedFlags: derived,

    derivedDeptId: user?.dept === "All" ? "All" : deptIds[0] || "",
  };
};

// Monotonic counter so that if several profile fetches overlap (App's auth
// listener, LoginPage, ProtectedRoute re-validation, tab-resume refresh), only
// the most recently STARTED request is allowed to write to the store. Without
// this, a slow request started before a renewal payment could land last and
// overwrite fresh "subscription_active: true" data with stale data.
let profileFetchSeq = 0;
// Seq of the latest NON-silent fetch; only that one may clear profileIsPending.
let blockingFetchSeq = 0;

const useAuth = create((set, get) => ({
  token: (() => {
    try {
      return secureLocalStorage.getItem("token");
    } catch {
      return null;
    }
  })(),
  isFileViewerOpen: false,
  screenshotDetected: false,
  users: [],
  profileData: (() => {
    try {
      const raw = secureLocalStorage.getItem("profileData");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return normalizeUser(parsed);
    } catch (e) {
      console.warn("Failed to hydrate profileData from storage", e);
      return null;
    }
  })(),
  // profileData: null,
  profileIsPending: true,
  // Epoch ms of the last SUCCESSFUL /auth/getProfileInfo/ response in this tab.
  // profileData hydrated from secureLocalStorage above may be days old (e.g. it
  // still says the subscription is expired after the customer renewed from a
  // different device), so 0 here means "cached, not yet verified with server".
  profileFetchedAt: 0,
  location: null,
  error: {},
  coordinates: [],
  filteredData: [],

  setToken: (newToken) => {
    set({ token: newToken });
    secureLocalStorage.setItem("token", JSON.stringify(newToken));
  },
  openFileViewer: () => set({ isFileViewerOpen: true }),
  closeFileViewer: () => set({ isFileViewerOpen: false }),
  setScreenshotDetected: (value) => set({ screenshotDetected: value }),
  setUsers: (value) => set({ users: value }),
  setProfileData: (valueOrUpdater) => {
    set((state) => {
      const nextValue =
        typeof valueOrUpdater === "function"
          ? valueOrUpdater(state.profileData)
          : valueOrUpdater;

      const normalized = normalizeUser(nextValue);

      try {
        if (normalized) {
          secureLocalStorage.setItem("profileData", JSON.stringify(normalized));
        } else {
          secureLocalStorage.removeItem("profileData");
        }
      } catch (e) {
        console.warn("Failed to persist profileData", e);
      }

      return { profileData: normalized };
    });
  },

  // setProfileData: (value) => {
  //     const normalized = normalizeUser(value);
  //     set({ profileData: normalized });
  //     try {
  //       if (normalized) {
  //         secureLocalStorage.setItem("profileData", JSON.stringify(normalized));
  //       } else {
  //         secureLocalStorage.removeItem("profileData");
  //       }
  //     } catch (e) {
  //       console.warn("Failed to persist profileData", e);
  //     }
  //   },
  // setProfileData: (value) => set({ profileData: value }),
  setProfileIsPending: (value) => set({ profileIsPending: value }),
  setLocation: (value) => set({ location: value }),
  setError: (value) => set({ error: value }),
  setCoordinates: (value) => set({ coordinates: value }),
  setFilteredData: (value) => set({ filteredData: value }),

  detectPrintScreen: (event) => {
    console.log("Key pressed:", event.key, event.code, event);
    const { key, code, metaKey, shiftKey, ctrlKey, altKey } = event;

    switch (true) {
      case metaKey && shiftKey && key === "3":
      case metaKey && shiftKey && key === "4":
      case metaKey && shiftKey && key === "6":
      case ctrlKey && key === "p":
      case altKey && (key === "PrintScreen" || key === "Insert"):
      case metaKey && (key === "PrintScreen" || key === "Insert"):
      case altKey && metaKey && (key === "PrintScreen" || key === "Insert"):
      case key === "PrintScreen" ||
        key === "Insert" ||
        code === "PrintScreen" ||
        code === 44:
      case metaKey && shiftKey && key === "s":
      case metaKey && shiftKey:
        event.preventDefault();
        console.log(`${event.key} pressed!`);

        useAuth.setState({ screenshotDetected: true });

        // Remove the blur class after 3 seconds
        setTimeout(() => {
          useAuth.getState().checkClipboardForImage();
          useAuth.setState({ screenshotDetected: false });
        }, 3000);

        useAuth.getState().monitorClipboard();
        useAuth.getState().checkClipboardForImage();
        useAuth.getState().screenshotAlert();
        break;

      default:
        break;
    }
  },

  handleContextMenu: (event) => {
    // Temporarily disabled to allow inspect element during development
    // event.preventDefault();
    // console.log("Right-click prevented!");
  },

  monitorClipboard: async () => {
    // Delay to allow the clipboard to update
    await new Promise((resolve) => setTimeout(resolve, 100));

    navigator.clipboard
      .read()
      .then((clipText) =>
        console.log("Clipboard content changed:", clipText[0].types)
      )
      .catch((error) => console.error("Error reading clipboard:", error));
  },

  checkClipboardForImage: async () => {
    await new Promise((resolve) => setTimeout(resolve, 200));
    try {
      const data = await navigator.clipboard.read();
      const types = data[0].types;
      let hasImageType = false;
      for (const type of types) {
        if (type.startsWith("image")) {
          hasImageType = true;
          break;
        }
      }
      if (hasImageType) {
        console.log("Screenshot captured.", types);

        // Assuming the first item in the clipboard data is the image
        const imageBlob = await data[0].getType("image/png");
        // await uploadImageToSupabase(imageBlob);
        console.log("Image blob:", imageBlob);
        useAuth.setState({ screenshotDetected: true });

        await useAuth.getState().screenshotAlert();

        setTimeout(() => set({ screenshotDetected: false }), 3000);
      } else {
        useAuth.setState({ screenshotDetected: false });
      }
    } catch (error) {
      console.error("Error reading clipboard:", error);
    }
  },

  screenshotAlert: async (fileId) => {
    try {
      const token = auth.currentUser && (await auth.currentUser.getIdToken());

      if (fileId) {
        const res = await api.post(
          `/file/logEvent/${fileId}?event=screenshot`,
          {
            event: "screenshot",
            idToken: token,
          }
        );
        console.log("screenshot log :", res);
      }
    } catch (error) {
      console.log(error);
    }
  },

  fetchRecentFiles: async () => {
    try {
      const recentFilesFromBackend = await api.get(`/file/files`);
      if (recentFilesFromBackend) {
        const mappedFiles = recentFilesFromBackend.data.map(async (file) => {
          try {
            const { data } = await supabase.storage
              .from("avatar")
              .getPublicUrl(file.owner_email);

            return {
              id: file.id,
              name: file.name.substring(0, 80),
              size: useAuth.getState().formatFileSize(file.metadata.size),
              dept: file.dept_name,
              publicUrl: data.publicUrl,
              owner: file.owner_email,
              mimetype: file.metadata.mimetype,
              status: "Team",
              security: "Enhanced",
              drive_link: file?.drive_link,
              drive_file_id: file?.drive_file_id,
              lastUpdate: new Date(file.metadata.lastModified).toLocaleString(
                "en-IN",
                {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                }
              ),
            };
          } catch (error) {
            console.log("Error while getting public URL:", error);
            return null;
          }
        });

        const resolvedFiles = await Promise.all(mappedFiles);
        useAuth.setState({
          filteredData: resolvedFiles.filter((file) => file !== null),
        });
        // console.log("Files:", filteredFiles);
        // localStorage.setItem("filteredFiles", JSON.stringify(filteredFiles));
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  },
  //   fetchRecentFiles();
  // }, []);

  formatFileSize: (sizeInBytes) => {
    const units = ["B", "KB", "MB", "GB"];
    let size = sizeInBytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return size.toFixed(2) + " " + units[unitIndex];
  },

  /**
   * Fetches the canonical profile (incl. org-level subscription_active) from
   * the backend and stores it.
   *
   * @param {Object}  [opts]
   * @param {boolean} [opts.silent=false] When true, does NOT toggle
   *   profileIsPending. Use for background refreshes (tab resume, checkout
   *   page re-check) so ProtectedRoute doesn't unmount the page into <Loading/>.
   * @returns {Promise<Object|null>} the fresh normalized user, or null if the
   *   fetch was skipped/failed (in which case the store is left unchanged).
   */
  fetchProfileData: async ({ silent = false } = {}) => {
    const seq = ++profileFetchSeq;
    if (!silent) {
      blockingFetchSeq = seq;
      set({ profileIsPending: true });
    }

    try {
      const user = auth.currentUser;
      // Check BEFORE calling getIdToken() — the old code called
      // auth.currentUser.getIdToken() first, which threw a TypeError when
      // there was no user and silently left stale cached data in place.
      if (!user) return null;

      // `user.emailVerified` is a snapshot Firebase persisted on THIS device
      // at sign-in time. For org admins the backend marks the email verified
      // during payment (activate_subscription_firestore → update_user), so a
      // device that was already signed in keeps seeing `false` forever and
      // every profile fetch was skipped — leaving stale cached profileData
      // (e.g. "subscription expired") in charge of routing. Refresh the user
      // record and token once before giving up.
      let forceRefresh = false;
      if (!user.emailVerified) {
        try {
          await user.reload();
          forceRefresh = true; // new token must carry the updated claim
        } catch (e) {
          console.warn("Failed to reload Firebase user", e);
        }
        if (!auth.currentUser?.emailVerified) {
          console.log("Skipping profile fetch — user not verified");
          return null;
        }
      }

      const token = await user.getIdToken(forceRefresh);
      if (!token) return null;

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/auth/getProfileInfo/`,
        { idToken: token }
      );

      const normalizedUser = normalizeUser(res.data.user);

      // A newer fetch was started while this one was in flight — let that one
      // write to the store instead of overwriting it with possibly older data.
      // The caller still gets what the server returned for its own request.
      if (seq !== profileFetchSeq) return normalizedUser;

      set({ profileData: normalizedUser, profileFetchedAt: Date.now() });

      try {
        secureLocalStorage.setItem(
          "profileData",
          JSON.stringify(normalizedUser)
        );
      } catch (e) {
        console.warn("Failed to persist profileData", e);
      }

      return normalizedUser;
    } catch (error) {
      console.log("error occurred while fetching profile data", error);
      return null;
    } finally {
      // Only the latest blocking request clears the pending flag, so an older
      // request finishing early can't make route guards evaluate before the
      // fresh data arrives.
      if (!silent && seq === blockingFetchSeq) {
        set({ profileIsPending: false });
      }
    }
  },
  setSessionToken: (newToken) => {
    set({ token: newToken });
    secureLocalStorage.setItem("token", JSON.stringify(newToken));
  },

  refreshAccessToken: async () => {
    try {
      console.log("Refreshing token...");
      let token = JSON.parse(secureLocalStorage.getItem("token"));
      if (!token) {
        console.log("No token available");
        return;
      }
      const refresh_token = token.session.refresh_token;
      const { data, error } = await supabase.auth.refreshSession({
        refresh_token,
      });
      if (data && data.session) {
        console.log("Token Refreshed Successfully", data);
        useAuth.setSessionToken(data);
      } else if (error || !data) {
        console.error("Error refreshing token:", error);
      }
    } catch (error) {
      // handle error accordingly
      console.error("Error refreshing token:", error);
    }
  },

  getGeolocation: () => {
    let watchId;
    if ("geolocation" in navigator) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const { coords } = position;
          useAuth.setState({
            location: {
              latitude: coords.latitude,
              longitude: coords.longitude,
            },
          });
        },
        (error) => useAuth.setState({ error: error.message })
      );
    } else {
      useAuth.setState({
        error: "Geolocation is not supported in your browser.",
      });
    }
    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
    };
  },
  listLocations: async () => {
    try {
      const locations = await api.get(`/file/file/listLocation`);
      // console.log("locations :", locations.data.features);
      set({ coordinates: locations.data.features });
    } catch (error) {
      console.log("Error while listing location Coordinates.", error);
    }
  },

  togglePinFileList: (fileId) => {
    const { profileData } = get();

    const pinned = profileData?.pinned_files || [];
    const isPinned = pinned.includes(fileId);

    set({
      profileData: {
        ...profileData,
        pinned_files: isPinned
          ? pinned.filter((id) => id !== fileId)
          : [...pinned, fileId],
      },
    });

    // const updated_files = isPinned
    //   ? pinned.filter((file) => file.id !== fileId)
    //   : [...pinned, fileId];

    // set({
    //   profileData: {
    //     ...profileData,
    //     pinned_files: updated_files,
    //   },
    // });
  },
}));

export default useAuth;
